import { FileOutlined, LinkOutlined } from '@ant-design/icons';

import { Button, Empty, Skeleton } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { useDiaChiXemTruoc, useNoiDungVanBan } from '../hooks/useXemTruoc';
import type { TepXemTruoc } from '../types';

/**
 * Xem trước tệp gốc của chứng từ hay tài liệu trong kho: ảnh, PDF trong `<iframe>`, văn bản thuần
 * trong `<pre>`. Định dạng khác (Word, Excel) không xem được trong trình duyệt nên chỉ có liên kết mở.
 * Đủ bốn trạng thái: đang tải, lỗi (kèm thử lại), không có tệp, có tệp.
 */
export function XemTruocTep({
  tep,
  dangTai,
  loi,
  onThuLai,
  tenTep,
  mime,
}: {
  tep: TepXemTruoc | undefined;
  dangTai: boolean;
  loi: unknown;
  onThuLai: () => void;
  /** Tên và kiểu theo chứng từ — liên kết ký có thể không mang. */
  tenTep: string | null;
  mime: string | null;
}) {
  const { t } = useT();
  const dichLoi = useLoi();
  const diaChi = useDiaChiXemTruoc(tep);
  const vanBan = useNoiDungVanBan(tep);
  const kieu = (tep?.mime_type || mime || '').toLowerCase();
  const ten = tenTep || tep?.file_name || t('ocr.tepGoc');

  if (dangTai) return <Skeleton className="h-full min-h-[320px] w-full" />;
  if (loi) {
    return (
      <div role="alert" className="flex flex-col items-start gap-3 p-5">
        <p className="text-sm text-danger-700">{t('ocr.loiTaiTep', { loi: dichLoi(loi) })}</p>
        <Button variant="outline" size="sm" onClick={onThuLai}>
          {t('chung.thuLai')}
        </Button>
      </div>
    );
  }
  if (!tep || !diaChi) return <Empty icon={FileOutlined} title={t('ocr.khongCoTep')} />;

  if (kieu.startsWith('image/')) {
    return (
      <div className="flex h-full items-start justify-center overflow-auto p-4">
        <img src={diaChi} alt={ten} className="max-w-full rounded border border-default" />
      </div>
    );
  }
  if (kieu === 'application/pdf') {
    return <iframe src={diaChi} title={ten} className="h-full min-h-[480px] w-full border-0" />;
  }
  if (kieu.startsWith('text/')) {
    return (
      <pre className="h-full overflow-auto whitespace-pre-wrap break-words p-5 font-mono text-xs leading-relaxed text-content-secondary">
        {vanBan.data ?? ''}
      </pre>
    );
  }
  return (
    <Empty
      icon={FileOutlined}
      title={t('ocr.khongXemTruocDuoc')}
      description={t('ocr.khongXemTruocDuocMo')}
      action={
        <a href={diaChi} target="_blank" rel="noreferrer" download={ten}>
          <Button variant="outline" size="md" icon={<LinkOutlined />}>
            {t('ocr.moTep')}
          </Button>
        </a>
      }
    />
  );
}
